import { Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@app/core/app.config';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { Entity } from '@app/core/model/entities/entity';
import { AbstractDatagrid } from '@app/shared/components/datagrid/abstract-datagrid';
import {
  ServerSideIndicatorStatusPanelComponent
} from '@app/shared/components/indicator-status-panel/server-side-indicator-status-panel/server-side-indicator-status-panel.component';
import { filterEntityByPaths, getLocale, getValue, setValue, toSafeId } from '@app/shared/extra/utils';
import { ColDefContext } from '@app/shared/grid/column-builder';
import {
  Filter,
  getConvertedAgFilterModel,
  getConvertedAgGroupKeys,
  getConvertedAgRowGroupCols,
  getConvertedAgSortModel,
  getConvertedAgValueCols,
  getCssDependentAggregations,
  SelectParams
} from '@app/shared/grid/datasource-utils';
import { DatagridBuilder } from '@services/grid-config.service';
import { GridOptions, IServerSideDatasource, RowSelectionOptions } from 'ag-grid-community';
import { IServerSideGetRowsRequest, SetFilterValuesFuncParams } from 'ag-grid-enterprise';
import { BehaviorSubject, distinct, from, fromEventPattern, ignoreElements, Observable, of } from 'rxjs';
import { debounceTime, delayWhen, filter, first, map, mergeMap, switchMap, tap, toArray } from 'rxjs/operators';

@Component({template: '', standalone: false})
export abstract class ServerSideDatagrid extends AbstractDatagrid implements OnInit {

  private serverSideInitialized = false;
  protected previousFilters: Filter[] = [];
  protected rowData: any[] = [];
  protected datasource: IServerSideDatasource;
  public globalSearchControl: FormControl<string> = new FormControl(null);
  public hasGlobalSearchChanged: boolean = false;
  private currentSelectParams: SelectParams | null = null;
  private readonly requestPending = new BehaviorSubject<number>(0);

  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected appConfig: AppConfig = inject(AppConfig);

  protected constructor() {
    super();
    this.datasource = {
      getRows: (params) => {
        this.requestPending.next(this.requestPending.value + 1);
        const selectParams = this.getConvertedAgSelectParams(params.request);
        selectParams.filters.push(...this.defaultFilters);

        // Remove aggregation on the group to retrieve
        if (selectParams.groups?.length && selectParams.aggregations?.length) {
          selectParams.aggregations = selectParams.aggregations.filter(
            aggregation => !selectParams.groups.includes(aggregation.field)
          );
        }

        // Keep a reference to the current select params
        this.currentSelectParams = selectParams;

        this.getGridEntities(selectParams).pipe(
          first(),
          map(entities => this.transformGroupEntities(selectParams, entities)),
          tap(() => this.requestPending.next(this.requestPending.value - 1))
        )
          .subscribe(entities => {
            entities.length ? params.api.hideOverlay() : params.api.showNoRowsOverlay();
            params.success({rowData: entities});
          });

        const panel: ServerSideIndicatorStatusPanelComponent = this.gridApi.getStatusPanel('indicators');
        // we do not update the indicators if something other than the filters changed
        if (panel && (this.haveFiltersChanged(selectParams) || this.hasGlobalSearchChanged)) {
          panel.updateFilteredValuesSubject.next({
            filters: selectParams.filters ?? [],
            search: selectParams.search ?? null
          });
          this.hasGlobalSearchChanged = false;
        }
      }
    };
  }

  /**
   * Subscribe to changes in the route to reload server side data.
   * Subscribe to value changes from the global search input to trigger server side data reloading.
   */
  public ngOnInit(): void {
    this.route.data
      .pipe(
        filter(() => !!this.gridApi && this.serverSideInitialized),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.gridApi.refreshServerSide({purge: true}));

    this.globalSearchControl.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.hasGlobalSearchChanged = true;
        this.gridApi.refreshServerSide({purge: true});
      });
  }

  /**
   * Transform entities to remove irrelevant properties when grouping and set unique ID for groups
   * @param selectParams SelectParams that was used to fetch the entities.
   * @param entities Entities that were fetched from the server.
   * @return Enities with groups transformed to be fonctional with the grid.
   */
  private transformGroupEntities(selectParams: SelectParams, entities: Entity[]): Entity[] {
    // Return entities as-is if it's not groups
    if (selectParams.groupKeys.length == selectParams.groups.length) return entities;

    // For each group we will remove everything excepted id and grouped properties
    return entities.map(e => {
      // If group is null remove its value
      if (e.id === 'null') {
        setValue(e, selectParams.groups[selectParams.groupKeys.length].split('.'), this.appConfig.GRID_NULL_FLAG);
      }
      // Set unique ID for groups
      setValue(e, ['id'], this.createGroupId(selectParams, e)); // Set unique ID for groups
      // Remove irrelevant properties
      return filterEntityByPaths(e, [...selectParams.groups, ...selectParams.aggregations.map(agg => agg.field), 'id']);
    });
  }


  /**
   * Load all entities matching the last select params used by the grid.
   * Fetch all group entities if the last select params has groups.
   * @return An Observable emitting an array of all fetched entities.
   */
  protected loadAllEntitiesForGridExport(): Observable<void> {
    const groupDepth = new BehaviorSubject<number>(1);

    return this.loadAllEntities({...this.currentSelectParams, groupKeys: []}).pipe(
      map(entities => this.transformGroupEntities(this.currentSelectParams, entities)),
      tap(entities => this.gridApi.applyServerSideRowData({successParams: {rowData: entities}})),
      filter(() => this.currentSelectParams.groups.length > 0),
      // Expand all groups at each depth if there are any
      switchMap(() => groupDepth.asObservable()),
      tap(() => this.gridApi.expandAll()),
      // Wait for all groups at the current depth to be expanded
      delayWhen(() => this.requestPending.pipe(
          debounceTime(100),
          filter(count => count === 0)
        )
      ),
      // Increment group depth
      tap(() => {
        if (groupDepth.value < this.currentSelectParams.groups.length) {
          groupDepth.next(groupDepth.value + 1);
        } else {
          groupDepth.complete();
        }
      }),
      ignoreElements()
    );
  }

  /**
   * Filters that have to be applied by default when requesting entities to display in the grid.
   * @return A list of default filters.
   * @protected
   */
  protected get defaultFilters(): Filter[] {
    return [];
  }

  /**
   * Converts an AG Grid server-side row request into an API-compatible SelectParams.
   * Handles pagination, sorting, filtering, grouping, and search value conversion.
   *
   * @param request The AG Grid server-side request containing pagination, sort, and filter data.
   * @return A SelectParams object compatible with the API query structure.
   * @protected
   */
  protected getConvertedAgSelectParams(request: IServerSideGetRowsRequest): SelectParams {
    const colDefs = this.gridApi.getColumnDefs();
    const groups = getConvertedAgRowGroupCols(request.rowGroupCols, colDefs);
    const groupKeys = getConvertedAgGroupKeys(request.groupKeys, groups, colDefs);
    const aggregations = getCssDependentAggregations(colDefs).concat(
      getConvertedAgValueCols(request.valueCols, colDefs)
    );
    const selectParams: SelectParams = {
      start: request.startRow,
      count: request.endRow - request.startRow,
      sorts: getConvertedAgSortModel(request.sortModel, colDefs),
      filters: getConvertedAgFilterModel(request.filterModel, colDefs),
      groups: groups,
      groupKeys: groupKeys,
      aggregations: aggregations,
      locale: getLocale()
    };

    // If group request add count
    if (selectParams.groupKeys.length < selectParams.groups.length) {
      selectParams.aggregations.push({field: 'properties.count', operation: 'COUNT'});
    }

    if (this.globalSearchControl.value) {
      selectParams.search = `*${this.globalSearchControl.value.replace(/[?*\\]/g, '\\$&')}*`;
    }
    return selectParams;
  }

  /**
   * Check if any filter on the grid has been changed.
   * @param params Select Params applied to the grid.
   * @return True if filters have changed, false otherwise
   */
  protected haveFiltersChanged(params: SelectParams): boolean {
    const filtersChanged = !params.filters || JSON.stringify(this.previousFilters) !== JSON.stringify(params.filters);
    this.previousFilters = params.filters;
    return filtersChanged;
  }

  /**
   * Setup gridOptions with default settings for SSRM rendering
   * @param extraGridOptions extra options to override the default settings
   * @protected
   */
  protected override initGridOptions(extraGridOptions: GridOptions): void {
    this.gridOptions = {
      ...this.gridOptionsService.staticGridOptions,
      // SSRM options
      cacheBlockSize: this.appConfig.GRID_CACHE_BLOCK_SIZE,
      rowBuffer: this.appConfig.PAGE_LOAD_THRESHOLD,
      rowModelType: 'serverSide',
      getChildCount: (data: any): number => data?.properties?.count ?? 0,
      // extra options
      ...extraGridOptions,
      cellSelection: {
        suppressMultiRanges: false,
        handle: {mode: 'range'}
      },
      rowSelection: <RowSelectionOptions>{
        ...<RowSelectionOptions>this.gridOptionsService.staticGridOptions.rowSelection,
        checkboxes: true,
        headerCheckbox: false,
        selectAll: 'all', // 'filtered' is not permitted with SSRM, logic have to be implemented manually
        isRowSelectable: node => !node.group,
        hideDisabledCheckboxes: true,
        ...<RowSelectionOptions>extraGridOptions.rowSelection
      },
      defaultColDef: {
        ...this.gridOptionsService.staticGridOptions.defaultColDef,
        ...extraGridOptions.defaultColDef
      },
      onGridPreDestroyed: () => this.gridApi.stopEditing(true)
    };
  }

  /**
   * Retrieves grid entities from the API or data source based on the given SelectParams.
   *
   * @param selectParams The parameters defining pagination, filters, sorting, and grouping.
   * @return An Observable emitting an array of entities corresponding to the grid data.
   * @protected
   */
  protected abstract getGridEntities(selectParams: SelectParams): Observable<Entity[]>;

  /**
   * Retrieves all grid entities from the API or data source based on the given SelectParams.
   *
   * @param selectParams The parameters defining pagination, filters, sorting, and grouping.
   * @return An Observable emitting an array of entities corresponding to the grid data.
   */
  protected abstract loadAllEntities(selectParams: SelectParams): Observable<Entity[]>;

  /**
   * Retrieve all distinct values for the provided fieldPath to initialise the column’s SetFilterValues.
   * @param fieldPath Field's path.
   * @return Observable that emits an array of Entity.
   */
  protected abstract getSetFiltersValues(fieldPath: string[]): Observable<Entity[]>;

  /**
   * Override agSetColumnFilter settings to fetch all filter values asynchronously.
   * @param gridConfig Grid's configuration with all column definitions.
   */
  protected configureAsyncSetFilter(gridConfig: DatagridBuilder): void {
    // For each column that has a filter set to agSetColumnFilter, set the function that will retrieve the distinct values
    return gridConfig.columnDefs.forEach(col => {
      if (col.filter !== 'agSetColumnFilter') return;

      const context = col.context as ColDefContext;
      const fieldType = context.fieldConfig.field.fieldType;

      let values: any[] | ((params: SetFilterValuesFuncParams) => void);
      switch (fieldType) {
        case FieldTypeEnum.RADIOBUTTON:
          values = [true, false, ''];
          break;
        case FieldTypeEnum.LIST:
        case FieldTypeEnum.NUMERIC_LIST:
          values = context.fieldConfig.field.fieldValues.concat('');
          break;
        default:
          values = (params: SetFilterValuesFuncParams) => {
            this.getSetFiltersValues(context.fieldConfig.fieldPath).pipe(
              // Emit each entity
              mergeMap(from),
              // Extract value from entity
              map(entity => getValue(entity, context.fieldConfig.fieldPath)),
              // Flatten array
              mergeMap(value => {
                if (Array.isArray(value)) {
                  return value.length > 0 ? from(value) : of(null);
                }
                return of(value);
              }),
              distinct(),
              toArray()
            )
              .subscribe(values => params.success(values));
          };
          break;
      }

      col.filterParams = {
        ...col.filterParams,
        values
      };
    });
  }

  /**
   * Build a stable, unique ID for a server-side group row.
   *
   * The ID is based on the current group path (groupKeys) and the grouped
   * column value for the current level. Example:
   *   [] + 'FR'         -> 'FR'
   *   ['FR'] + '2025'   -> 'FR:2025'
   *   ['FR', '2025'] + 'Q1' -> 'FR:2025:Q1'
   *
   * If the group column cannot be resolved, the fallback is the joined groupKeys.
   *
   * @param selectParams Current SelectParams used for this block.
   * @param entity       Entity returned by the API. For group rows, entity.id is an empty string.
   * @return A stable string ID for the group row.
   */
  private createGroupId(selectParams: SelectParams, entity: Entity): string {
    const {groupKeys, groups} = selectParams;
    const level = groupKeys.length;

    const fieldPath = groups[level];
    if (!fieldPath) {
      // No group column for this level, fallback to the group path
      return groupKeys.join(':');
    }

    const rawValue = getValue(entity, fieldPath.split('.')) ?? 'null';
    const prefix = groupKeys.length ? groupKeys.join(':') + ':' : 'global';
    return toSafeId(prefix + entity.id + JSON.stringify(rawValue));
  }

  /**
   * Initialize SSRM datasource once everything (columns/state) is ready.
   */
  protected initServerSideDatasource(): void {
    this.gridApi.setGridOption('serverSideDatasource', this.datasource);
    this.serverSideInitialized = true;
  }

  /**
   * Returns all routes that should be refreshed for the given entities.
   *
   * For each entity:
   * - If it's a leaf, the parent group route is used.
   * - All prefixes of the route are added (including [] for root).
   *
   * @param entities Entities to inspect.
   * @returns Array of unique routes to refresh.
   */
  protected getRoutesToRefresh(entities: Entity[]): string[][] {
    const routeKeys: Record<string, string[]> = {};

    for (const entity of entities) {
      const node = this.gridApi.getRowNode(entity.id);
      if (!node) continue;

      const groupNode = node.group ? node : node.parent;
      const fullRoute = groupNode?.getRoute?.() ?? [];

      // Add all prefixes, including root []
      for (let i = 0; i <= fullRoute.length; i++) {
        const route = fullRoute.slice(0, i);
        const routeKey = JSON.stringify(route);
        if (!routeKeys.hasOwnProperty(routeKey)) {
          routeKeys[routeKey] = route;
        }
      }
    }
    return Object.values(routeKeys);
  }

  /**
   * Apply grid changes for the given entities.
   * - If there is no grouping, runs the root transaction callback.
   * - If there is grouping, refreshes only impacted routes.
   *
   * @param entities Entities concerned by the change.
   * @param applyRootTransaction Called when there is no grouping (root store).
   */
  protected applyEntityChanges(
    entities: Entity[],
    applyRootTransaction: (entities: Entity[]) => void
  ): void {
    if (!entities || entities.length === 0) return;

    if (this.gridApi.getRowGroupColumns().length === 0) {
      applyRootTransaction(entities);
      this.gridApi.hideOverlay();
      return;
    }

    // Grouped case: refresh only impacted routes
    const routes = this.getRoutesToRefresh(entities);
    const routeSubject = new BehaviorSubject(routes.pop());

    routeSubject.asObservable().pipe(
      tap(route => this.gridApi.refreshServerSide({route, purge: false})),
      delayWhen(() =>
        fromEventPattern(
          (handler) => this.gridApi.addEventListener('storeRefreshed', handler),
          (handler) => { if (!this.gridApi.isDestroyed()) this.gridApi.removeEventListener('storeRefreshed', handler); }
        )
      ),
      tap(() => {
        const next = routes.pop();
        next ? routeSubject.next(next) : routeSubject.complete();
      }),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe({complete: () => this.gridApi.hideOverlay()});
  }
}
